<template>
    <kiosk-layout>
        <div class="flex flex-col gap-12 lg:flex-row">
            <div class="shrink-0 lg:w-60">
                <kiosk-teams-sidebar v-model="fetchParams"></kiosk-teams-sidebar>
            </div>
            <div class="w-full">
                <kiosk-teams-controls v-model="fetchParams" @reset="resetFilters"></kiosk-teams-controls>
                <page-loading v-if="loading && !list.length"></page-loading>

                <empty-state
                    v-else-if="!list.length && !isEqual(defaultFilters(), fetchParams)"
                    @reset="resetFilters"
                />

                <div v-else-if="list.length">
                    <div class="text-xl font-extrabold">Results</div>
                    <kiosk-teams-table>
                        <kiosk-team-row
                            v-for="(row, index) in response?.data?.data || []"
                            v-model="(response?.data?.data || [])[index]"
                            :key="row.id"
                        ></kiosk-team-row>
                    </kiosk-teams-table>
                </div>

                <div v-else-if="isEqual(defaultFilters(), fetchParams)">
                    <div v-if="favorites.length" class="text-xl font-extrabold">Favorites</div>
                    <kiosk-teams-table v-if="favorites.length">
                        <kiosk-team-row
                            v-for="(row, index) in favorites"
                            v-model="favorites[index]"
                            :key="row.id"
                        ></kiosk-team-row>
                    </kiosk-teams-table>

                    <div class="text-xl font-extrabold">Recents</div>

                    <kiosk-teams-table>
                        <kiosk-team-row
                            v-for="(row, index) in recents"
                            v-model="recents[index]"
                            :key="row.id"
                        ></kiosk-team-row>
                    </kiosk-teams-table>
                </div>
            </div>
        </div>
    </kiosk-layout>
</template>

<script setup>
import EmptyState from '~/components/general/EmptyState.vue';
import { computed, ref, watch } from 'vue';
import PageLoading from '~/components/general/PageLoading.vue';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';
import KioskTeamRow from '~/components/kiosk/kiosk-teams/list/KioskTeamRow.vue';
import KioskTeamsControls from '~/components/kiosk/kiosk-teams/list/KioskTeamsControls.vue';
import KioskTeamsSidebar from '~/components/kiosk/kiosk-teams/details/KioskTeamsSidebar.vue';
import KioskTeamsTable from '~/components/kiosk/kiosk-teams/list/KioskTeamsTable.vue';
import { useToasted } from '~/composables/useToasted';
import { useVersionedRequest } from '~/composables/useVersionedRequest';
import { adminTeamsClient } from '~/services';
import { isEqual } from '~/utils/helpers';

const toasted = useToasted();

const defaultFilters = () => ({
    status: undefined,
    parent_id: undefined,
    query: '',
    sort_dir: 'desc',
    sort_field: 'id',
    per_page: 100,
});

const fetchParams = ref(defaultFilters());

const { loading, response, fetchData } = useVersionedRequest();

const list = computed(() => response.value?.data?.data || []);

watch(
    fetchParams,
    () => {
        filterData();
    },
    {
        deep: true,
    }
);

const resetFilters = () => {
    fetchParams.value = defaultFilters();
    filterData();
};

const filterData = () => {
    fetchData(adminTeamsClient.getTeams(fetchParams.value));
};

const favorites = ref([]);
const recents = ref([]);

// on first load favorite teams and recent teams are loaded
const loadInitialData = async () => {
    loading.value = true;

    try {
        const favoritesResponse = await adminTeamsClient.getTeams({ is_favorite: 1, per_page: 100 });
        const recentsResponse = await adminTeamsClient.getTeams({
            is_favorite: 0,
            sort_dir: 'desc',
            sort_field: 'id',
            per_page: 100,
        });

        favorites.value = favoritesResponse.data.data;
        recents.value = recentsResponse.data.data;
    } catch (e) {
        console.error(e);
        toasted.global.platform_error();
    } finally {
        loading.value = false;
    }
};

loadInitialData();
</script>
